import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';

import { ENABLE_ORDER_TRACKER_RA_IR } from '#client/constants/experimentsFlags';
import {
  FORMATION_TASK_TYPES,
  getOutstandingFormationTask,
  getRedirectServiceCode,
} from '#client/utils/formationPostPurchase';

import {
  ALLOWED_ORDER_TASKS,
  CHIP_COLORS,
  INFO_TOOLTIP_MESSAGES,
  ORDER_TASKS_UPSELLS,
  TASK_LINK_CONDITIONS,
  WFC_ADVANCED_UPSELL,
  WFC_ESSENTIAL_UPSELL,
} from './constants';

// Entitlement type constants
const ENTITLEMENT_WFC = 'worry_free_service';
const ENTITLEMENT_WFC_BASIC = 'worry_free_service_basic';
const ENTITLEMENT_WFC_ADVANCED = 'worry_free_service_advanced';

// Banking application statuses that should hide the banking task from OrderTracker
const FINAL_BANKING_STATUSES = ['approved', 'denied', 'canceled', 'expired', 'not_qualified', 'closed'];

const RA_IR_TASK_TYPES = ['registered_agent_service', 'wfg_initial_report'];
const IR_ALLOWED_JURISDICTIONS = ['AK', 'CA'];

const ANNUAL_REPORT_SERVICE_CODE = 'llc_annual_report';
const CORP_FORMATION_SERVICE_CODE = 'corp_formation';

export { FORMATION_TASK_TYPES };

/**
 * Whether a task's link should send the user to the v4 discovery route (`/journeys/next`)
 * instead of its legacy route. Requires both:
 *  - a per-service redirect rollout gate (e.g. journeys-v4-redirection-annual-report), independent
 *    of the broader v4 rollout so each redirect can ship/revert on its own schedule.
 *  - journeys-api-v4-services: the same { [serviceName]: [jurisdiction, ...] } allowlist
 *    journeys-api itself checks server-side (mirrors journeys-ui's useJourneyVersion.js) —
 *    a client-side mirror purely to avoid sending an ineligible pair down a route
 *    journeys-api would reject anyway.
 * Case-insensitive on both the jurisdiction and service name — the flag is hand-edited in the
 * LaunchDarkly dashboard, so neither side's casing is guaranteed to match what this reads.
 * Only the given service's key is read; any other keys in the flag value are ignored.
 * @param {string} serviceName - key in the journeys-api-v4-services flag value
 * @param {string|undefined} jurisdictionCode
 * @param {object} params
 * @param {boolean} params.isRedirectionEnabled - the service's redirect gate value
 * @param {object} params.v4ServicesByService - journeys-api-v4-services' value
 * @returns {boolean}
 */
export function isServiceV4Eligible(serviceName, jurisdictionCode, { isRedirectionEnabled, v4ServicesByService }) {
  if (!isRedirectionEnabled || !jurisdictionCode) {
    return false;
  }
  const byLowercaseService = Object.fromEntries(
    Object.entries(v4ServicesByService ?? {}).map(([key, value]) => [key.toLowerCase(), value]),
  );
  const allowedJurisdictions = byLowercaseService[serviceName.toLowerCase()];
  if (!Array.isArray(allowedJurisdictions)) {
    return false;
  }
  return allowedJurisdictions.some(
    (jurisdiction) => typeof jurisdiction === 'string' && jurisdiction.toLowerCase() === jurisdictionCode.toLowerCase(),
  );
}

export function isAnnualReportV4Eligible(jurisdictionCode, params) {
  return isServiceV4Eligible(ANNUAL_REPORT_SERVICE_CODE, jurisdictionCode, params);
}

export function isCorpFormationV4Eligible(jurisdictionCode, params) {
  return isServiceV4Eligible(CORP_FORMATION_SERVICE_CODE, jurisdictionCode, params);
}

const isIncompleteWithFormationBlocked = (item, _, { isFormationTaskIncomplete }) =>
  item.status === 'incomplete' && isFormationTaskIncomplete;

const isIncompleteWithoutFormation = (item, _, { hasFormationTask, formationStatus }) =>
  item.status === 'incomplete' && !hasFormationTask && formationStatus !== 'formed';

const isIncompleteWithFormationPostPurchaseOutstanding = (item, _, { isFormationPostPurchaseOutstanding }) =>
  item.status === 'incomplete' && isFormationPostPurchaseOutstanding;

const PREREQUISITE_TOOLTIP_CONFIG = {
  ein_creation: [
    {
      tooltipMessageId: 'dashboard.orderTracker.einPrerequisiteTooltip',
      shouldShow: isIncompleteWithFormationBlocked,
    },
    {
      tooltipMessageId: 'dashboard.orderTracker.formationRequiredTooltip',
      shouldShow: isIncompleteWithoutFormation,
    },
  ],
  business_license_report: [
    {
      tooltipMessageId: 'dashboard.orderTracker.blrPrerequisiteTooltip',
      shouldShow: isIncompleteWithFormationBlocked,
    },
    {
      tooltipMessageId: 'dashboard.orderTracker.formationRequiredTooltip',
      shouldShow: isIncompleteWithoutFormation,
    },
  ],
  operating_agreement: [
    {
      tooltipMessageId: 'dashboard.orderTracker.oaPrerequisiteTooltip',
      shouldShow: isIncompleteWithFormationBlocked,
    },
    {
      tooltipMessageId: 'dashboard.orderTracker.formationRequiredTooltip',
      shouldShow: isIncompleteWithoutFormation,
    },
  ],
  // No formationRequiredTooltip branch here: that copy is EIN-specific and reads as
  // self-referential on this row. A standalone RA with no formation task just links.
  registered_agent_service: [
    {
      tooltipMessageId: 'dashboard.orderTracker.raPrerequisiteTooltip',
      shouldShow: isIncompleteWithFormationBlocked,
    },
  ],
  wfg_initial_report: [
    {
      tooltipMessageId: 'dashboard.orderTracker.irPrerequisiteTooltip',
      shouldShow: isIncompleteWithFormationPostPurchaseOutstanding,
    },
  ],
};

/**
 * Check if we should fetch banking application status for this task
 * @param {object} item - Order task item
 * @returns {boolean} Whether to fetch banking application status
 */
export function shouldFetchBankingStatus(item) {
  return item.type === 'zb_banking_preferred' && item.status === 'pending';
}

/**
 * Check if a banking task should be hidden from OrderTracker
 * @param {object} item - Order task item
 * @param {string} bankingApplicationStatus - Banking application status
 * @returns {boolean} Whether to hide this banking task
 */
export function shouldHideBankingTask(item, bankingApplicationStatus) {
  if (!bankingApplicationStatus) {
    return false;
  }
  return item.type === 'zb_banking_preferred' && FINAL_BANKING_STATUSES.includes(bankingApplicationStatus);
}

/**
 * A business entity can end up with more than one order_task row for the same task `type`
 * (e.g. a plan migration cancels the old task and creates a new one instead of replacing it
 * in place). Only the most recent row per type+jurisdiction reflects current state, so older
 * rows must not reach the status/chip logic below — a stale 'canceled' row otherwise survives
 * the 'complete' filter and renders as a red "canceled" chip next to (or instead of) the real,
 * current status (GROWTH-5452). Grouping includes `jurisdictionCode` so legitimate concurrent
 * tasks of the same type in different states (e.g. change_of_registered_agent) aren't collapsed
 * into one. Only dedupes when every task sharing a group has a `createdAt` to compare; callers
 * that don't fetch `createdAt` (e.g. EnterpriseOrderTracker's fulfillments) are unaffected,
 * since these are not known to exhibit the same historical duplicate-row pattern.
 * @param {Array} orderTasks - Raw order tasks from the API
 * @returns {Array} orderTasks with only the newest row per type+jurisdiction where recency is determinable
 */
function dedupeStaleOrderTasks(orderTasks) {
  const groupsByTypeAndJurisdiction = new Map();
  orderTasks.forEach((item) => {
    const key = `${item.type}|${item.jurisdictionCode ?? ''}`;
    const group = groupsByTypeAndJurisdiction.get(key);
    if (group) {
      group.push(item);
    } else {
      groupsByTypeAndJurisdiction.set(key, [item]);
    }
  });

  return Array.from(groupsByTypeAndJurisdiction.values()).flatMap((group) => {
    if (group.length === 1 || !group.every((item) => item.createdAt)) {
      return group;
    }
    return [group.reduce((latest, item) => (new Date(item.createdAt) > new Date(latest.createdAt) ? item : latest))];
  });
}

/**
 * Returns filtered and sorted order task items for the standard OrderTracker and the EnterpriseOrderTracker.
 * @param {Array} orderTasks - Raw order tasks from the API
 * @param {object} options
 * @param {boolean} options.enableRaIr - Include RA and IR task types (experiment flag)
 * @param {boolean} options.keepLiveStatus - Keep a live registered agent's 'active' status instead
 *   of collapsing it to 'complete' (used by the enterprise tracker, which shows live status)
 * @returns {Array} Filtered/sorted order tasks with normalized status and chip color
 */
export function getTimelineItems(orderTasks, options) {
  const { enableRaIr, keepLiveStatus } = options || {};
  const validStatuses = Object.keys(CHIP_COLORS);
  return dedupeStaleOrderTasks(orderTasks)
    .map((item) => {
      const isLiveRegisteredAgent = item.type === 'registered_agent_service' && item.status === 'active';
      const isActiveBanking = item.type === 'zb_banking_preferred' && item.status === 'active';
      // The standard tracker shows a live registered agent or active banking as a completed step (so it is hidden).
      const status = !keepLiveStatus && (isLiveRegisteredAgent || isActiveBanking) ? 'complete' : item.status;
      return { ...item, status };
    })
    .filter((item) => {
      if (!ALLOWED_ORDER_TASKS.includes(item.type)) {
        return false;
      }
      if (!validStatuses.includes(item.status)) {
        return false;
      }
      if (item.status === 'complete') {
        return false;
      }
      if (RA_IR_TASK_TYPES.includes(item.type) && !enableRaIr) {
        return false;
      }
      if (item.type === 'wfg_initial_report' && !IR_ALLOWED_JURISDICTIONS.includes(item.jurisdictionCode)) {
        return false;
      }
      return true;
    })
    .map((item) => ({ ...item, color: CHIP_COLORS[item.status] }))
    .sort((a, b) => ALLOWED_ORDER_TASKS.indexOf(a.type) - ALLOWED_ORDER_TASKS.indexOf(b.type));
}

export function useGetTimelineItems(orderTasks) {
  const enableRaIr = useExperimentFlag(ENABLE_ORDER_TRACKER_RA_IR, false);
  return getTimelineItems(orderTasks, { enableRaIr });
}

export function getTimelineItemsWithRedirectLinks(orderTasks, getRedirectUrl, { formationStatus } = {}) {
  if (!getRedirectUrl) {
    return orderTasks.map((item) => ({
      ...item,
      hasExternalLink: false,
      linkTo: '',
    }));
  }

  const hasFormationTask = orderTasks.some((t) => FORMATION_TASK_TYPES.includes(t.type));
  const isFormationTaskIncomplete = orderTasks.some(
    (t) => FORMATION_TASK_TYPES.includes(t.type) && t.status === 'incomplete',
  );
  const isFormationPostPurchaseOutstanding = Boolean(getOutstandingFormationTask(orderTasks));
  const tooltipContext = {
    hasFormationTask,
    isFormationTaskIncomplete,
    isFormationPostPurchaseOutstanding,
    formationStatus,
  };

  return orderTasks.map((item) => {
    const linkOpts = { orderUuid: item.orderUuid, jurisdictionCode: item.jurisdictionCode };
    const redirectUrlByType = getRedirectUrl(getRedirectServiceCode(item), linkOpts);
    const serviceCode = item.data?.service;
    const redirectUrlByService = serviceCode ? getRedirectUrl(serviceCode, linkOpts) : null;
    const redirectUrl = redirectUrlByType || redirectUrlByService || '';
    const conditionFn = TASK_LINK_CONDITIONS[item.type];
    const hasExternalLink = conditionFn ? conditionFn(item, orderTasks, { formationStatus }) : Boolean(redirectUrl);
    const tooltipCandidates = PREREQUISITE_TOOLTIP_CONFIG[item.type] || [];
    const matchedTooltip = tooltipCandidates.find((c) => c.shouldShow(item, hasExternalLink, tooltipContext));
    const hoverTooltipMessageId = matchedTooltip?.tooltipMessageId;

    return {
      ...item,
      hasExternalLink,
      ...(hoverTooltipMessageId && { hoverTooltipMessageId }),
      linkTo: item.status === 'incomplete' && hasExternalLink ? redirectUrl : '',
    };
  });
}

export function getUpsellItems(type, entitlements, jurisdictionCode) {
  if (!entitlements) {
    return [];
  }

  const typeUpsells = ORDER_TASKS_UPSELLS[type] || [];
  const upsellsToProcess = [...typeUpsells]; // Create a copy to work with

  // Special handling for worry-free service upsells
  const hasWorryfreeBasic = entitlements.has(ENTITLEMENT_WFC_BASIC);
  const hasWorryfreeService = entitlements.has(ENTITLEMENT_WFC);
  const hasWorryfreeAdvanced = entitlements.has(ENTITLEMENT_WFC_ADVANCED);

  const wfcIndex = upsellsToProcess.findIndex((upsell) => upsell.entitlement === ENTITLEMENT_WFC);
  if (wfcIndex !== -1) {
    if (hasWorryfreeAdvanced) {
      // Already has the top tier — no WFC upsell to offer
      upsellsToProcess.splice(wfcIndex, 1);
    } else if (hasWorryfreeService) {
      // User has Essential but not Advanced — offer the Advanced upgrade
      upsellsToProcess[wfcIndex] = WFC_ADVANCED_UPSELL;
    } else if (hasWorryfreeBasic) {
      // User has Basic but not Essential — point to the Essential tab, not the one they already own
      upsellsToProcess[wfcIndex] = WFC_ESSENTIAL_UPSELL;
    }
  }

  // Now filter out upsells the user already has
  let filteredUpsells = upsellsToProcess.filter((upsell) => !entitlements.has(upsell.entitlement));

  // Filter out upsells that are not allowed for the jurisdiction
  filteredUpsells = filteredUpsells.filter((upsell) => {
    if (upsell.allowedJurisdictions) {
      return upsell.allowedJurisdictions.includes(jurisdictionCode);
    }
    return true;
  });

  // Limit to max 3 upsells
  return filteredUpsells.slice(0, 3);
}

/**
 * Resolves the info-tooltip description for a task, preferring a jurisdiction-specific
 * message where the task has one (Initial Report, whose deadline varies by state).
 * @param {object} item - Order task item
 * @returns {string|undefined} Message id, or undefined when the task has no tooltip
 */
export function getInfoTooltipDescriptionId(item) {
  const config = INFO_TOOLTIP_MESSAGES[item.type];
  return config?.descriptionIdByJurisdiction?.[item.jurisdictionCode] || config?.descriptionId;
}

export function getTitleMessageSuffix(item) {
  if (item.type === 'change_of_registered_agent' && item.jurisdictionCode) {
    return ` - ${item.jurisdictionCode}`;
  }
  return '';
}
